import { useContext, useState } from 'react';
import {
    Grid,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Typography,
    Divider,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { AppContext, SttDialog } from '../../sporttia/all';
import { SttSelectCity } from '../../sporttia/selectors/SttSelectCity';
import { useLoader } from '../../lib/hooks';
import useUsersService from '../../services/UsersService';
import { getObjectWithoutEmptyStrings } from '../../lib/utils';
import { translatePlaceholders } from '../../lib/translatePlaceholders';
import { PERMISSIONS } from '../../lib/permissions';

type City = {
    id: number;
    name: string;
};

type CreateUserParams = {
    name: string;
    surname1: string;
    surname2?: string;
    dni?: string;
    idCity?: number;
    address?: string;
    postalCode?: string;
    email?: string;
    mobile?: string;
    birthday?: string;
    gender?: 'UNKNOWN' | 'MALE' | 'FEMALE';
    iban?: string;
    password?: string;
    sendWelcomen: boolean;
};

type User = {
    login: string;
};

type UserCreationDialogProps = {
    open: boolean;
    onClose: () => void;
    onCreate?: (user: User) => void;
};

type ValidationFieldError = {
    field: string;
    message: string;
};

type CreateUserError = {
    error: {
        msg: string;
        errors?: ValidationFieldError[];
    };
};

type CreateUserResponse = {
    user?: {
        login: string;
    };
    error?: {
        msg: string;
        errors?: ValidationFieldError[];
    };
};

type FormState = {
    name: string;
    surname1: string;
    surname2: string;
    dni: string;
    birthday: Date | null;
    gender: 'UNKNOWN' | 'MALE' | 'FEMALE';
    email: string;
    mobile: string;
    city: City | null;
    address: string;
    postalCode: string;
    iban: string;
    password: string;
    sendWelcomen: boolean;
};

const initialFormState: FormState = {
    name: '',
    surname1: '',
    surname2: '',
    dni: '',
    birthday: null,
    gender: 'UNKNOWN',
    email: '',
    mobile: '',
    city: null,
    address: '',
    postalCode: '',
    iban: '',
    password: '',
    sendWelcomen: false,
};

export default function UserCreationDialog({
    open,
    onClose,
    onCreate,
}: UserCreationDialogProps): React.ReactElement {
    const { t } = useTranslation();
    const cxt = useContext(AppContext)!;
    const usersService = useUsersService();
    const [formData, setFormData] = useState<FormState>(initialFormState);
    const [lastUserCreated, setLastUserCreated] = useState<User | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleApiValidationError = (apiError: CreateUserError): void => {
        const fieldErrors: Record<string, string> = {};
        apiError.error.errors?.forEach((err) => {
            fieldErrors[err.field] = translatePlaceholders(err.message, t);
        });
        if (Object.keys(fieldErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        cxt?.showMessage('E', translatePlaceholders(apiError.error.msg, t));
    };

    const createMutation = useMutation<
        CreateUserResponse,
        CreateUserError,
        { params: CreateUserParams }
    >({
        mutationFn: (params: { params: CreateUserParams }) =>
            usersService.create(params.params),
        onSuccess: (data: CreateUserResponse) => {
            if (data?.user) {
                setLastUserCreated(data.user);
                onCreate?.(data.user);
            } else if (data?.error) {
                handleApiValidationError(data as CreateUserError);
            }
        },
        onError: handleApiValidationError,
    });

    const [, loader] = useLoader([createMutation.status]);

    const handleChange = (field: keyof FormState, value: unknown): void => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const validateForm = (): boolean => {
        const requiredErrorMessage = t('validation.required');
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = requiredErrorMessage;
        }
        if (!formData.surname1.trim()) {
            newErrors.surname1 = requiredErrorMessage;
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const clearFieldError = (field: string): void => {
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleSubmit = (): void => {
        if (!validateForm()) {
            return;
        }

        const params = getObjectWithoutEmptyStrings({
            name: formData.name,
            surname1: formData.surname1,
            surname2: formData.surname2,
            dni: formData.dni,
            birthday: formData.birthday
                ? format(formData.birthday, 'yyyy-MM-dd')
                : undefined,
            gender: formData.gender || 'UNKNOWN',
            email: formData.email,
            mobile: formData.mobile,
            idCity: formData.city?.id,
            address: formData.address,
            postalCode: formData.postalCode,
            iban: formData.iban,
            password: formData.password,
            sendWelcomen: formData.sendWelcomen,
        }) as CreateUserParams;

        createMutation.mutate({ params });
    };

    const resetForm = (): void => {
        setFormData(initialFormState);
        setLastUserCreated(null);
        setErrors({});
    };

    return (
        <SttDialog
            title={t('userCreation.title')}
            maxWidth="md"
            open={open}
            onClose={onClose}
            content={
                <>
                    {loader}
                    <Grid container spacing={2}>
                        {/* Personal Information */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('Name')}
                                value={formData.name}
                                error={Boolean(errors.name)}
                                helperText={errors.name ?? ''}
                                onChange={(e) => {
                                    handleChange('name', e.target.value);
                                    if (e.target.value.trim()) {
                                        clearFieldError('name');
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                required
                                label={t('Surname1')}
                                value={formData.surname1}
                                error={Boolean(errors.surname1)}
                                helperText={errors.surname1 ?? ''}
                                onChange={(e) => {
                                    handleChange('surname1', e.target.value);
                                    if (e.target.value.trim()) {
                                        clearFieldError('surname1');
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Surname2')}
                                value={formData.surname2}
                                onChange={(e) =>
                                    handleChange('surname2', e.target.value)
                                }
                            />
                        </Grid>

                        <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('DNI')}
                                value={formData.dni}
                                onChange={(e) =>
                                    handleChange('dni', e.target.value)
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <DatePicker
                                label={t('Birthday')}
                                value={formData.birthday}
                                onChange={(value) =>
                                    handleChange('birthday', value)
                                }
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('Gender')}</InputLabel>
                                <Select
                                    value={formData.gender}
                                    label={t('Gender')}
                                    onChange={(e) =>
                                        handleChange('gender', e.target.value)
                                    }
                                >
                                    <MenuItem value="UNKNOWN">
                                        {t('userCreation.genderSelect')}
                                    </MenuItem>
                                    <MenuItem value="MALE">
                                        {t('MALE')}
                                    </MenuItem>
                                    <MenuItem value="FEMALE">
                                        {t('FEMALE')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Mobile')}
                                value={formData.mobile}
                                onChange={(e) =>
                                    handleChange('mobile', e.target.value)
                                }
                            />
                        </Grid>

                        {/* Contact */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Email')}
                                type="email"
                                value={formData.email}
                                error={Boolean(errors.email)}
                                helperText={errors.email ?? ''}
                                onChange={(e) => {
                                    handleChange('email', e.target.value);
                                    clearFieldError('email');
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('IBAN')}
                                value={formData.iban}
                                onChange={(e) =>
                                    handleChange('iban', e.target.value)
                                }
                            />
                        </Grid>

                        {/* Address Section */}
                        <Grid size={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('Address')}
                                </Typography>
                            </Divider>
                        </Grid>

                        <SttSelectCity
                            grid
                            caption={t('City')}
                            name="city"
                            defVal={formData.city?.name || ''}
                            onChange={() => {}}
                            onSelect={(event) =>
                                handleChange('city', event.value)
                            }
                            xs={12}
                            md={5}
                        />
                        <Grid size={{ xs: 12, md: 5 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Address')}
                                value={formData.address}
                                onChange={(e) =>
                                    handleChange('address', e.target.value)
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('PostalCode')}
                                value={formData.postalCode}
                                onChange={(e) =>
                                    handleChange('postalCode', e.target.value)
                                }
                                autoComplete="off"
                            />
                        </Grid>

                        {/* Account */}
                        <Grid size={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {t('Account')}
                                </Typography>
                            </Divider>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={t('Password')}
                                type="password"
                                value={formData.password}
                                onChange={(e) =>
                                    handleChange('password', e.target.value)
                                }
                                autoComplete="new-password"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.sendWelcomen}
                                        onChange={(e) =>
                                            handleChange(
                                                'sendWelcomen',
                                                e.target.checked,
                                            )
                                        }
                                    />
                                }
                                label={t('NotificationUserSC')}
                            />
                        </Grid>
                    </Grid>

                    <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
                        {t('popup.purse.alertNoEmail')}
                    </Alert>

                    {lastUserCreated && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {t('popup.purse.userCreated')}
                            <br />
                            <strong>{t('User')}:</strong>{' '}
                            {lastUserCreated.login}
                        </Alert>
                    )}
                </>
            }
            buttons={[
                {
                    show: lastUserCreated === null,
                    type: 'save',
                    onClick: handleSubmit,
                    permission: PERMISSIONS.CUSTOMER.CREATE,
                },
                {
                    show: lastUserCreated !== null,
                    caption: t('popup.purse.cleanAndAnother'),
                    onClick: resetForm,
                },
            ]}
        />
    );
}
